/**
 * Admin: agent messages. The library agents send from (Notifications tab): categories and the CRM
 * roles that see them, the messages and their wording, limits, and the switch for the CRM's
 * free-text notifications. And the send card on a client's profile (every category, sent as the
 * admin). Names and figures are filled in by the server; nothing here sends text of its own.
 */
import { useMemo, useState, type ReactNode } from 'react';
import {
  AGENT_BODY_MAX,
  AGENT_CONDITIONS,
  AGENT_LINKS,
  AGENT_ROLE_LABELS,
  AGENT_ROLES,
  AGENT_TITLE_MAX,
  AGENT_VARS,
  fmtDateTime,
  parseAgentMessage,
  type AgentCategory,
  type AgentMessage,
  type AgentMessageSettings,
  type AgentOffer,
  type AgentPick,
  type AgentRole,
  type AgentSend,
} from '@platform/shared';
import { cx, Spinner } from '../components/ui';
import { api, ApiError } from '../lib/api';
import { useLoad } from '../stores/account';

const input = 'h-9 w-full rounded-lg border border-line bg-bg px-3 text-[13px] text-fg outline-none focus:border-line-strong placeholder:text-muted';
const area = 'min-h-[64px] w-full rounded-lg border border-line bg-bg px-3 py-2 text-[13px] text-fg outline-none focus:border-line-strong placeholder:text-muted';
const btn = 'h-8 rounded-md border border-line-strong px-3 text-xs font-semibold text-fg disabled:opacity-50';
const primary = 'h-9 rounded-lg bg-accent px-4 text-xs font-semibold text-white disabled:opacity-50';
const err = (e: unknown) => (e instanceof ApiError ? e.message : 'Something went wrong');

interface Library {
  settings: AgentMessageSettings;
  categories: AgentCategory[];
  messages: (AgentMessage & { isNew: boolean })[];
  stats: { messageId: string; sent: number; delivered: number; opened: number }[];
}

function Field({ label, children, className, hint }: { label: string; children: ReactNode; className?: string; hint?: ReactNode }) {
  return (
    <label className={cx('flex flex-col gap-1 text-xs text-muted', className)}>
      <span className="flex justify-between gap-2">
        <span>{label}</span>
        {hint}
      </span>
      {children}
    </label>
  );
}

const PICK_LABEL: Record<AgentPick, string> = { none: 'Nothing', trade: "One of the client's profitable trades", period: 'A period (today, last week, this month, last month)' };

// ---------- the library (Notifications tab) ----------

export function AgentMessagesSection() {
  const { data, reload, error } = useLoad<Library>('/api/admin/agent-messages');
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [editing, setEditing] = useState<string | null>(null); // message id, or `new:<categoryId>`
  const run = async (fn: () => Promise<unknown>, ok?: string) => {
    setMsg(null);
    try {
      await fn();
      await reload();
      if (ok) setMsg({ ok: true, text: ok });
      return true;
    } catch (e) {
      setMsg({ ok: false, text: err(e) });
      return false;
    }
  };

  return (
    <section className="flex flex-col gap-4 rounded-xl border border-line bg-panel p-4">
      <div>
        <h2 className="m-0 text-sm font-semibold">Agent messages</h2>
        <p className="m-0 mt-0.5 text-xs text-muted">
          Push messages staff send a client from the CRM&apos;s Actions tab or a client&apos;s profile here. Agents pick a message and can&apos;t type their own text. Names and figures are filled in from the client&apos;s account.
        </p>
      </div>
      {error && <p className="m-0 text-xs text-down">{error}</p>}
      {msg && <p className={cx('m-0 text-xs', msg.ok ? 'text-up' : 'text-down')}>{msg.text}</p>}
      {!data ? (
        <Spinner />
      ) : (
        <>
          <Rules settings={data.settings} onSave={(s) => run(() => api.put('/api/admin/agent-messages/settings', s), 'Rules saved')} />
          {data.categories.map((c, i) => (
            <CategoryBlock
              key={c.id}
              cat={c}
              first={i === 0}
              last={i === data.categories.length - 1}
              messages={data.messages.filter((m) => m.categoryId === c.id)}
              categories={data.categories}
              stats={data.stats}
              editing={editing}
              setEditing={setEditing}
              run={run}
              onMove={(dir) => {
                const ids = data.categories.map((x) => x.id);
                const j = i + dir;
                [ids[i], ids[j]] = [ids[j]!, ids[i]!];
                void run(() => api.put('/api/admin/agent-messages/order', { kind: 'categories', ids }));
              }}
            />
          ))}
          <NewCategory onAdd={(c) => run(() => api.post('/api/admin/agent-messages/categories', c), 'Category added')} />
        </>
      )}
    </section>
  );
}

function Rules({ settings, onSave }: { settings: AgentMessageSettings; onSave: (s: AgentMessageSettings) => Promise<boolean> }) {
  const [f, setF] = useState(settings);
  const dirty = JSON.stringify(f) !== JSON.stringify(settings);
  return (
    <div className="flex flex-wrap items-end gap-3 rounded-lg border border-line p-3">
      <Field label="Messages per client per day" className="w-44">
        <input type="number" min={1} max={20} className={input} value={f.perClientPerDay} onChange={(e) => setF({ ...f, perClientPerDay: Number(e.target.value) })} id="am-per-client" />
      </Field>
      <Field label="Messages per agent per day" className="w-44">
        <input type="number" min={1} max={10000} className={input} value={f.perAgentPerDay} onChange={(e) => setF({ ...f, perAgentPerDay: Number(e.target.value) })} id="am-per-agent" />
      </Field>
      <label className="flex max-w-sm items-start gap-2 pb-1 text-xs">
        <input type="checkbox" checked={f.freeText} onChange={(e) => setF({ ...f, freeText: e.target.checked })} id="am-free-text" className="mt-0.5" />
        <span>
          Let the CRM send free-text notifications
          <span className="block text-[11px] text-muted">Off: the CRM can only send the messages below.</span>
        </span>
      </label>
      <button type="button" className={primary} disabled={!dirty} onClick={() => void onSave(f)}>
        Save rules
      </button>
    </div>
  );
}

function RolePicker({ roles, onChange }: { roles: AgentRole[]; onChange: (r: AgentRole[]) => void }) {
  return (
    <div className="flex flex-wrap gap-3 text-xs">
      {AGENT_ROLES.map((r) => (
        <label key={r} className="flex items-center gap-1.5">
          <input type="checkbox" checked={roles.includes(r)} onChange={(e) => onChange(e.target.checked ? [...roles, r] : roles.filter((x) => x !== r))} />
          {AGENT_ROLE_LABELS[r]}
        </label>
      ))}
    </div>
  );
}

function CategoryBlock(p: {
  cat: AgentCategory;
  first: boolean;
  last: boolean;
  messages: Library['messages'];
  categories: AgentCategory[];
  stats: Library['stats'];
  editing: string | null;
  setEditing: (id: string | null) => void;
  run: (fn: () => Promise<unknown>, ok?: string) => Promise<boolean>;
  onMove: (dir: -1 | 1) => void;
}) {
  const { cat, run } = p;
  const [f, setF] = useState({ name: cat.name, description: cat.description, roles: cat.roles });
  const [open, setOpen] = useState(false);
  const dirty = f.name !== cat.name || f.description !== cat.description || f.roles.join() !== cat.roles.join();
  return (
    <div className="flex flex-col gap-2 rounded-lg border border-line p-3">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="m-0 text-sm font-semibold">{cat.name}</h3>
        <span className="text-xs text-muted">{cat.roles.length ? cat.roles.map((r) => AGENT_ROLE_LABELS[r]).join(', ') : 'No CRM role (Admin only)'}</span>
        <div className="flex-1" />
        <button type="button" className={btn} disabled={p.first} onClick={() => p.onMove(-1)} aria-label={`Move ${cat.name} up`}>
          ↑
        </button>
        <button type="button" className={btn} disabled={p.last} onClick={() => p.onMove(1)} aria-label={`Move ${cat.name} down`}>
          ↓
        </button>
        <button type="button" className={btn} onClick={() => setOpen((o) => !o)}>
          {open ? 'Close' : 'Edit category'}
        </button>
      </div>
      <p className="m-0 text-xs text-muted">{cat.description}</p>
      {open && (
        <div className="flex flex-col gap-2 rounded-lg bg-raised p-3">
          <div className="grid gap-2 sm:grid-cols-2">
            <Field label="Name">
              <input className={input} value={f.name} maxLength={40} onChange={(e) => setF({ ...f, name: e.target.value })} id={`cat-name-${cat.id}`} />
            </Field>
            <Field label="Description">
              <input className={input} value={f.description} maxLength={160} onChange={(e) => setF({ ...f, description: e.target.value })} id={`cat-desc-${cat.id}`} />
            </Field>
          </div>
          <span className="text-xs text-muted">CRM roles that see this category</span>
          <RolePicker roles={f.roles} onChange={(roles) => setF({ ...f, roles })} />
          <div className="flex gap-2">
            <button type="button" className={btn} disabled={!dirty} onClick={() => void run(() => api.put(`/api/admin/agent-messages/categories/${cat.id}`, f), 'Category saved').then((ok) => ok && setOpen(false))}>
              Save category
            </button>
            {!p.messages.length && (
              <button type="button" className={cx(btn, 'border-down text-down')} onClick={() => void run(() => api.del(`/api/admin/agent-messages/categories/${cat.id}`), 'Category deleted')}>
                Delete category
              </button>
            )}
          </div>
        </div>
      )}
      <div className="flex flex-col divide-y divide-line">
        {p.messages.map((m) => {
          const s = p.stats.find((x) => x.messageId === m.id);
          return p.editing === m.id ? (
            <MessageEditor key={m.id} message={m} categories={p.categories} onClose={() => p.setEditing(null)} run={run} />
          ) : (
            <div key={m.id} className="flex flex-wrap items-start gap-3 py-2">
              <div className={cx('min-w-0 flex-1', !m.enabled && 'opacity-50')}>
                <div className="text-[13px] font-semibold">
                  {m.title} {m.isNew && <span className="ml-1 rounded bg-accent/15 px-1.5 py-0.5 text-[10px] font-semibold text-accent">New</span>}
                  {!m.enabled && <span className="ml-1 rounded bg-raised px-1.5 py-0.5 text-[10px] text-muted">Off</span>}
                </div>
                <div className="text-xs text-muted">{m.body}</div>
                <div className="mt-0.5 text-[11px] text-muted">
                  Opens {AGENT_LINKS.find((l) => l.path === m.link)?.label ?? m.link} · {AGENT_CONDITIONS.find((c) => c.key === m.condition)?.label} · again after {m.cooldownDays} days
                </div>
              </div>
              <div className="num w-36 text-right text-[11px] text-muted">
                {s ? (
                  <>
                    {s.sent} sent · {s.delivered} delivered
                    <br />
                    {s.sent ? Math.round((s.opened / s.sent) * 100) : 0}% opened (30 days)
                  </>
                ) : (
                  'Not sent in 30 days'
                )}
              </div>
              <button type="button" className={btn} onClick={() => p.setEditing(m.id)}>
                Edit
              </button>
            </div>
          );
        })}
        {p.editing === `new:${cat.id}` ? (
          <MessageEditor categories={p.categories} categoryId={cat.id} onClose={() => p.setEditing(null)} run={run} />
        ) : (
          <div className="pt-2">
            <button type="button" className={btn} onClick={() => p.setEditing(`new:${cat.id}`)}>
              New message in {cat.name}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function MessageEditor({
  message,
  categoryId,
  categories,
  onClose,
  run,
}: {
  message?: AgentMessage;
  categoryId?: string;
  categories: AgentCategory[];
  onClose: () => void;
  run: (fn: () => Promise<unknown>, ok?: string) => Promise<boolean>;
}) {
  const [f, setF] = useState({
    categoryId: message?.categoryId ?? categoryId ?? categories[0]?.id ?? '',
    title: message?.title ?? '',
    body: message?.body ?? '',
    link: message?.link ?? '/account',
    condition: message?.condition ?? 'any',
    pick: message?.pick ?? 'none',
    cooldownDays: message?.cooldownDays ?? 7,
    enabled: message?.enabled ?? true,
  });
  const [focus, setFocus] = useState<'title' | 'body'>('body');
  const parsed = useMemo(() => parseAgentMessage(f), [f]);
  const vars = AGENT_VARS.filter((v) => !v.pick || v.pick === f.pick);
  const save = () =>
    void run(() => (message ? api.put(`/api/admin/agent-messages/messages/${message.id}`, f) : api.post('/api/admin/agent-messages/messages', f)), message ? 'Message saved' : 'Message added').then((ok) => ok && onClose());
  const id = message?.id ?? 'new';
  return (
    <div className="my-2 flex flex-col gap-3 rounded-lg bg-raised p-3">
      <div className="grid gap-2 sm:grid-cols-2">
        <Field label="Category">
          <select className={input} value={f.categoryId} onChange={(e) => setF({ ...f, categoryId: e.target.value })} id={`m-cat-${id}`}>
            {categories.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Opens">
          <select className={input} value={f.link} onChange={(e) => setF({ ...f, link: e.target.value })} id={`m-link-${id}`}>
            {AGENT_LINKS.map((l) => (
              <option key={l.path} value={l.path}>
                {l.label} · {l.path}
              </option>
            ))}
          </select>
        </Field>
      </div>
      <Field label="Title" hint={<span className={cx(f.title.length > AGENT_TITLE_MAX && 'text-down')}>{f.title.length}/{AGENT_TITLE_MAX}</span>}>
        <input className={input} value={f.title} onFocus={() => setFocus('title')} onChange={(e) => setF({ ...f, title: e.target.value })} id={`m-title-${id}`} />
      </Field>
      <Field label="Text" hint={<span className={cx(f.body.length > AGENT_BODY_MAX && 'text-down')}>{f.body.length}/{AGENT_BODY_MAX}</span>}>
        <textarea className={area} value={f.body} onFocus={() => setFocus('body')} onChange={(e) => setF({ ...f, body: e.target.value })} id={`m-body-${id}`} />
      </Field>
      <div className="flex flex-wrap items-center gap-1.5 text-[11px]">
        <span className="text-muted">Insert into the {focus === 'title' ? 'title' : 'text'}:</span>
        {vars.map((v) => (
          <button key={v.key} type="button" title={v.label} className="rounded border border-dashed border-line-strong px-1.5 py-0.5 font-mono text-accent" onClick={() => setF({ ...f, [focus]: `${f[focus]}${f[focus].endsWith(' ') || !f[focus] ? '' : ' '}{${v.key}}` })}>
            + {`{${v.key}}`}
          </button>
        ))}
      </div>
      <div className="grid gap-2 sm:grid-cols-3">
        <Field label="Offered for">
          <select className={input} value={f.condition} onChange={(e) => setF({ ...f, condition: e.target.value as AgentMessage['condition'] })} id={`m-cond-${id}`}>
            {AGENT_CONDITIONS.map((c) => (
              <option key={c.key} value={c.key}>
                {c.label}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Agent also picks">
          <select className={input} value={f.pick} onChange={(e) => setF({ ...f, pick: e.target.value as AgentPick })} id={`m-pick-${id}`}>
            {(['none', 'trade', 'period'] as const).map((k) => (
              <option key={k} value={k}>
                {PICK_LABEL[k]}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Same client again after (days)">
          <input type="number" min={0} max={365} className={input} value={f.cooldownDays} onChange={(e) => setF({ ...f, cooldownDays: Number(e.target.value) })} id={`m-cool-${id}`} />
        </Field>
      </div>
      <label className="flex items-center gap-2 text-xs">
        <input type="checkbox" checked={f.enabled} onChange={(e) => setF({ ...f, enabled: e.target.checked })} id={`m-on-${id}`} />
        Available to agents
      </label>
      <div className="flex flex-wrap items-center gap-2">
        <button type="button" className={primary} disabled={!parsed.ok} onClick={save}>
          {message ? 'Save message' : 'Add message'}
        </button>
        <button type="button" className={btn} onClick={onClose}>
          Cancel
        </button>
        {message && (
          <button type="button" className={cx(btn, 'border-down text-down')} onClick={() => void run(() => api.del(`/api/admin/agent-messages/messages/${message.id}`), 'Message deleted').then((ok) => ok && onClose())}>
            Delete
          </button>
        )}
        {!parsed.ok && <span className="text-xs text-down">{parsed.error}</span>}
        {message && <span className="ml-auto text-[11px] text-muted">Version {message.version} · {message.updatedBy ?? 'migration'} · {fmtDateTime(message.updatedAt)}. Past sends keep their wording.</span>}
      </div>
    </div>
  );
}

function NewCategory({ onAdd }: { onAdd: (c: { name: string; description: string; roles: AgentRole[] }) => Promise<boolean> }) {
  const [f, setF] = useState<{ name: string; description: string; roles: AgentRole[] } | null>(null);
  if (!f)
    return (
      <div>
        <button type="button" className={btn} onClick={() => setF({ name: '', description: '', roles: [] })}>
          New category
        </button>
      </div>
    );
  return (
    <div className="flex flex-col gap-2 rounded-lg border border-dashed border-line p-3">
      <div className="grid gap-2 sm:grid-cols-2">
        <Field label="Name">
          <input className={input} value={f.name} maxLength={40} onChange={(e) => setF({ ...f, name: e.target.value })} id="new-cat-name" />
        </Field>
        <Field label="Description">
          <input className={input} value={f.description} maxLength={160} onChange={(e) => setF({ ...f, description: e.target.value })} id="new-cat-desc" />
        </Field>
      </div>
      <RolePicker roles={f.roles} onChange={(roles) => setF({ ...f, roles })} />
      <div className="flex gap-2">
        <button type="button" className={btn} disabled={!f.name.trim()} onClick={() => void onAdd(f).then((ok) => ok && setF(null))}>
          Add category
        </button>
        <button type="button" className={btn} onClick={() => setF(null)}>
          Cancel
        </button>
      </div>
    </div>
  );
}

// ---------- the send card (client profile) ----------

interface OffersView {
  client: { firstName: string; devices: { platform: string; standalone: boolean }[]; pushMuted: boolean; quietUntil: string | null };
  limits: { clientToday: number; clientMax: number; agentToday: number; agentMax: number };
  categories: (AgentCategory & { offers: AgentOffer[] })[];
}

const deviceLabel = (d: { platform: string; standalone: boolean }) => (d.platform === 'ios' ? (d.standalone ? 'iPhone · Home Screen app' : 'iPhone') : d.platform === 'android' ? 'Android' : 'Desktop');

export function AgentSendCard({ userId }: { userId: string }) {
  const { data, reload, error } = useLoad<OffersView>(`/api/admin/users/${userId}/agent-messages`);
  const hist = useLoad<{ data: AgentSend[] }>(`/api/admin/users/${userId}/agent-messages/history`);
  const [cat, setCat] = useState<string | null>(null);
  const [sel, setSel] = useState<string | null>(null);
  const [opt, setOpt] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  if (error) return <p className="m-0 text-xs text-down">{error}</p>;
  if (!data) return <Spinner />;
  const cats = data.categories.filter((c) => c.offers.length);
  const current = cats.find((c) => c.id === cat) ?? cats[0];
  const offer = current?.offers.find((o) => o.id === sel) ?? current?.offers.find((o) => !o.blocked) ?? current?.offers[0];
  const option = offer?.options.find((o) => o.id === opt) ?? offer?.options[0];
  const preview = option ? { title: option.title, body: option.body } : offer ? { title: offer.title, body: offer.body } : null;
  const send = async () => {
    if (!offer) return;
    setBusy(true);
    setMsg(null);
    try {
      const r = await api.post<{ pushQueued: number }>(`/api/admin/users/${userId}/agent-messages`, { messageId: offer.id, optionId: option?.id });
      setMsg({ ok: true, text: r.pushQueued ? `Sent to ${r.pushQueued} ${r.pushQueued === 1 ? 'device' : 'devices'}` : 'Sent to the bell (no device to push to)' });
      setOpt(null);
      await Promise.all([reload(), hist.reload()]);
    } catch (e) {
      setMsg({ ok: false, text: err(e) });
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="flex flex-col gap-3 rounded-lg border border-line p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="m-0 text-sm font-semibold">Send a push message</h3>
        <span className="text-[11px] text-muted">
          Sent today: {data.limits.clientToday} of {data.limits.clientMax} for this client
        </span>
      </div>
      <div className="flex flex-wrap gap-1.5 text-[11px]">
        {data.client.devices.length ? (
          data.client.devices.map((d, i) => (
            <span key={i} className="rounded bg-raised px-2 py-1">
              {deviceLabel(d)}
            </span>
          ))
        ) : (
          <span className="rounded bg-raised px-2 py-1 text-muted">No device with notifications on: the message goes to the bell only</span>
        )}
        {data.client.pushMuted && <span className="rounded bg-raised px-2 py-1 text-warn">Client muted these messages: bell only</span>}
        {data.client.quietUntil && <span className="rounded bg-raised px-2 py-1 text-warn">Quiet hours now: arrives {fmtDateTime(data.client.quietUntil)}</span>}
      </div>
      {!current ? (
        <p className="m-0 text-xs text-muted">No messages apply to this client right now.</p>
      ) : (
        <div className="grid gap-3 md:grid-cols-[150px_minmax(0,1fr)]">
          <div className="flex flex-row flex-wrap gap-1 md:flex-col" role="tablist" aria-label="Message categories">
            {cats.map((c) => (
              <button
                key={c.id}
                type="button"
                role="tab"
                aria-selected={c.id === current.id}
                className={cx('rounded-md border px-2.5 py-1.5 text-left text-xs', c.id === current.id ? 'border-accent bg-raised' : 'border-transparent hover:bg-raised')}
                onClick={() => {
                  setCat(c.id);
                  setSel(null);
                  setOpt(null);
                }}
              >
                <span className="block font-semibold">{c.name}</span>
                <span className="text-[11px] text-muted">
                  {c.offers.filter((o) => !o.blocked).length} of {c.offers.length} available
                </span>
              </button>
            ))}
          </div>
          <div className="flex min-w-0 flex-col gap-2">
            <p className="m-0 text-[11px] text-muted">{current.description}</p>
            {current.offers.map((o) => (
              <button
                key={o.id}
                type="button"
                disabled={!!o.blocked}
                className={cx('flex items-start gap-2 rounded-lg border p-2.5 text-left text-xs disabled:opacity-50', o.id === offer?.id ? 'border-accent' : 'border-line')}
                onClick={() => {
                  setSel(o.id);
                  setOpt(null);
                }}
              >
                <span className="min-w-0 flex-1">
                  <span className="block font-semibold">{o.title}</span>
                  <span className="text-muted">{o.body}</span>
                </span>
                {o.blocked ? <span className="shrink-0 text-[11px] text-warn">{o.blocked}</span> : o.isNew ? <span className="shrink-0 text-[11px] text-accent">New</span> : null}
              </button>
            ))}
            {offer && offer.pick !== 'none' && offer.options.length > 0 && (
              <Field label={offer.pick === 'trade' ? 'Which trade (profitable, live, last 7 days)' : 'Which period (only periods that ended in profit)'}>
                <select className={input} value={option?.id ?? ''} onChange={(e) => setOpt(e.target.value)} id="agent-pick">
                  {offer.options.map((o) => (
                    <option key={o.id} value={o.id}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </Field>
            )}
            {preview && (
              <div className="rounded-xl bg-raised p-3 text-xs">
                <div className="text-[10px] uppercase tracking-wide text-muted">Preview</div>
                <div className="font-semibold">{preview.title}</div>
                <div>{preview.body}</div>
              </div>
            )}
            <div className="flex flex-wrap items-center gap-2">
              <button type="button" className={primary} disabled={busy || !offer || !!offer.blocked} onClick={() => void send()}>
                {busy ? 'Sending…' : 'Send'}
              </button>
              {msg && <span className={cx('text-xs', msg.ok ? 'text-up' : 'text-down')}>{msg.text}</span>}
            </div>
          </div>
        </div>
      )}
      {!!hist.data?.data.length && (
        <div className="flex flex-col gap-1 border-t border-line pt-2">
          <span className="text-xs font-semibold">Sent to this client</span>
          {hist.data.data.slice(0, 5).map((s) => (
            <div key={s.id} className="flex justify-between gap-2 text-[11px]">
              <span className="min-w-0 truncate">
                {s.title} <span className="text-muted">· {s.agentName} · {fmtDateTime(s.createdAt)}</span>
              </span>
              <span className={cx('shrink-0', s.openedAt ? 'text-up' : 'text-muted')}>{s.openedAt ? 'Opened' : s.pushQueued ? 'Pushed' : 'Bell only'}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
